import apiClient from '../axiosConfig';
import { components } from '../types/openapi';

// Tipos del OpenAPI
type RoleResponse = components['schemas']['RoleResponse'];
type RoleSimpleResponse = components['schemas']['RoleSimpleResponse'];
type CreateRole = components['schemas']['CreateRole'];
type UpdateRole = components['schemas']['UpdateRole'];
type AddDeletePermissionRole = components['schemas']['AddDeletePermissionRole'];
type PaginationRoleResponse = components['schemas']['Pagination_RoleResponse_'];

/**
 * Servicio de gestión de roles
 * Maneja todas las operaciones CRUD de roles
 * Basado en los endpoints del OpenAPI
 */
class RoleService {
  /**
   * Obtener lista de roles
   * GET /api/users/roles/
   */
  async getRoles(params?: {
    limit?: number;
    name?: string;
  }): Promise<RoleResponse[]> {
    const response = await apiClient.get<RoleResponse[]>('/users/roles/', { params });
    return response.data;
  }

  /**
   * Obtener roles con paginación
   * GET /api/users/roles/pagination
   */
  async getRolesPaginated(params?: {
    page?: number;
    limit?: number;
    name?: string;
  }): Promise<PaginationRoleResponse> {
    const response = await apiClient.get<PaginationRoleResponse>('/users/roles/pagination', { params });
    return response.data;
  }

  /**
   * Obtener un rol por ID
   * GET /api/users/roles/{role_id}
   */
  async getRoleById(roleId: number): Promise<RoleResponse> {
    const response = await apiClient.get<RoleResponse>(`/users/roles/${roleId}`);
    return response.data;
  }

  /**
   * Crear un nuevo rol
   * POST /api/users/roles/
   */
  async createRole(roleData: CreateRole): Promise<RoleResponse> {
    const response = await apiClient.post<RoleResponse>('/users/roles/', roleData);
    return response.data;
  }

  /**
   * Actualizar un rol
   * PUT /api/users/roles/
   */
  async updateRole(roleData: UpdateRole): Promise<RoleResponse> {
    const response = await apiClient.put<RoleResponse>('/users/roles/', roleData);
    return response.data;
  }

  /**
   * Eliminar un rol
   * DELETE /api/users/roles/{role_id}
   */
  async deleteRole(roleId: number): Promise<void> {
    await apiClient.delete(`/users/roles/${roleId}`);
  }

  /**
   * Verificar si se puede eliminar un rol
   * GET /api/users/roles/check-delete/{role_id}
   */
  async checkDeleteRole(roleId: number): Promise<unknown> {
    const response = await apiClient.get(`/users/roles/check-delete/${roleId}`);
    return response.data;
  }

  /**
   * Cambiar estado de un rol (activar/desactivar)
   * POST /api/users/roles/change-state/{role_id}
   */
  async changeRoleState(roleId: number): Promise<RoleResponse> {
    const response = await apiClient.post<RoleResponse>(`/users/roles/change-state/${roleId}`);
    return response.data;
  }

  /**
   * Agregar permiso a un rol
   * PUT /api/users/roles/add-permission
   */
  async addPermissionToRole(data: AddDeletePermissionRole): Promise<RoleResponse> {
    const response = await apiClient.put<RoleResponse>('/users/roles/add-permission', data);
    return response.data;
  }

  /**
   * Eliminar permiso de un rol
   * PUT /api/users/roles/delete-permission
   */
  async removePermissionFromRole(data: AddDeletePermissionRole): Promise<RoleResponse> {
    const response = await apiClient.put<RoleResponse>('/users/roles/delete-permission', data);
    return response.data;
  }

  /**
   * Obtener todos los roles activos
   * GET /api/users/roles/all-active
   */
  async getAllActiveRoles(params?: {
    name?: string;
  }): Promise<RoleResponse[]> {
    const response = await apiClient.get<RoleResponse[]>('/users/roles/all-active', { params });
    return response.data;
  }

  /**
   * Obtener todos los roles (simples) para app
   * GET /api/users/roles/app/all-active
   */
  async getAllActiveRolesSimple(): Promise<RoleSimpleResponse[]> {
    const response = await apiClient.get<RoleSimpleResponse[]>('/users/roles/app/all-active');
    return response.data;
  }
}

export default new RoleService();

